/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    REMOVE_UNUSED_ADMIN_ICONS
 * Date:         2026-01-08
 * Modified By:  Cursor
 *
 * Description:
 * Drop unused FontAwesome icons to satisfy lint rules and keep the bundle lean.
 *
 * File Size Check:
 * - Current: 318
 * - Adding: ~24
 * - Projected: 342
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: n/a
 * - Tokens used: n/a
 *
 * Related Files:
 * - client/src/features/admin/users/UsersFilters.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_USERS_FILTERS_REFINED
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Refined UsersFilters component to resolve compilation issues.
 * Used standard function declaration and direct export.
 */

import * as React from "react";
import { Card, Row, Col, Form, Spinner, Modal, Table, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faEye, faExclamationTriangle, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
// [2026-01-08] icon cleanup: Remove unused FontAwesome icons so the import list matches actual usage.
import { UserFilters } from "../../../interfaces/adminUsers";
import { exportUsers, downloadCSV, deleteOrphanedData, previewOrphanedData, OrphanedDataCleanupResult } from "../../../services/adminUsersService";
import { UserFilterFields, UserDateFilters } from "./components/UserFilterFields";
import styles from "../AdminUsers.module.scss";
import UsersFiltersActions from "./UsersFiltersActions";
import AdminButton from "../../../components/admin/AdminButton";

interface UsersFiltersProps {
  params: UserFilters;
  onChange: (patch: Partial<UserFilters>) => void;
  onExport: () => void;
  onRefresh: () => void;
}

export default function UsersFilters({ params, onChange, onExport, onRefresh }: UsersFiltersProps) {
  const [searchValue, setSearchValue] = React.useState(params.q || "");
  const [isExporting, setIsExporting] = React.useState(false);
  const [cleanupLoading, setCleanupLoading] = React.useState(false);

  // [2025-12-30] Data cleanup: Add state for preview
  const [showPreviewModal, setShowPreviewModal] = React.useState(false);
  const [previewData, setPreviewData] = React.useState<OrphanedDataCleanupResult | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  // [2025-12-30] Data cleanup: Handler for preview button
  const handlePreviewOrphanedData = async () => {
    setPreviewLoading(true);
    setShowPreviewModal(true);
    
    try {
      const preview = await previewOrphanedData();
      setPreviewData(preview);
    } catch (error: any) {
      alert(`❌ Failed to preview orphaned data:\n${error.message}`);
      setShowPreviewModal(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // [2025-12-30] Data cleanup: Handler for actual cleanup (from preview modal)
  const handleConfirmCleanup = async () => {
    setCleanupLoading(true);
    
    try {
      const result = await deleteOrphanedData();
      
      const resultDetails = formatCleanupResults(result);
      
      const successMessage = 
        '✅ Orphaned Data Cleanup Completed!\n\n' +
        '═══════════════════════════════════════════\n\n' +
        'Collections Cleaned:\n\n' +
        (resultDetails || "No orphaned data found.") +
        '\n\n═══════════════════════════════════════════\n\n' +
        `📊 Total Records Deleted: ${result.totalOrphaned}\n` +
        `📁 Collections Processed: ${result.collectionsProcessed}\n` +
        `🔍 Collections with Orphans: ${result.collectionsWithOrphans}`;
      
      alert(successMessage);
      setShowPreviewModal(false);
      setPreviewData(null);
      onRefresh();
    } catch (error: any) {
      alert(`❌ Cleanup failed:\n${error.message}`);
    } finally {
      setCleanupLoading(false);
    }
  };

  // [2025-12-30] Data cleanup: Format cleanup results for display
  const formatCleanupResults = (results: any): string => {
    const lines: string[] = [];
    
    Object.entries(results).forEach(([key, value]) => {
      if (key === 'totalOrphaned' || key === 'totalDeleted') {
        return; // Skip, will show at bottom
      }
      if (key === 'collectionsProcessed' || key === 'collectionsWithOrphans') {
        return; // Skip metadata
      }
      if (key === 'userName' || key === 'userEmail') {
        return; // Skip user info
      }
      if (typeof value === 'number' && value > 0) {
        lines.push(`• ${key}: ${value} records`);
      }
    });
    
    return lines.join('\n');
  };

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      // [2026-06-23] pagination-fix: normalize empty values so "" (searchValue) and
      // undefined (params.q) compare equal. Previously "" !== undefined fired on every
      // setParams identity change (e.g. clicking a page), resetting page back to 1.
      if ((searchValue || undefined) !== (params.q || undefined)) {
        onChange({ q: searchValue || undefined, page: 1 });
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchValue, params.q, onChange]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value);
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") onChange({ q: searchValue || undefined, page: 1 });
  };
  const handleFilterChange = (key: any, value: any) => onChange({ [key as keyof UserFilters]: value || undefined, page: 1 });

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await exportUsers(params);
      downloadCSV(response.data, `users-export-${new Date().toISOString().split("T")[0]}.csv`);
      onExport();
    } catch (error) { console.error("Export failed:", error); } finally { setIsExporting(false); }
  };

  const handleCleanOrphanedData = async () => {
    const confirmMessage = 
      '⚠️  CRITICAL WARNING: Clean All Orphaned Data\n\n' +
      '═══════════════════════════════════════════\n\n' +
      'This operation will:\n\n' +
      '🔍 Scan ALL database collections\n' +
      '🗑️  Delete ALL records from deleted users\n' +
      '📊 Process broadcasts, inventory, messages, and more\n\n' +
      '═══════════════════════════════════════════\n\n' +
      '⚠️  THIS ACTION CANNOT BE UNDONE!\n\n' +
      'Recommended: Run discovery script first to see what will be deleted.\n\n' +
      'Do you want to continue?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCleanupLoading(true);
    try {
      const result = await deleteOrphanedData();
      
      const resultDetails = formatCleanupResults(result);
      
      const successMessage = 
        '✅ Orphaned Data Cleanup Completed!\n\n' +
        '═══════════════════════════════════════════\n\n' +
        'Collections Cleaned:\n\n' +
        (resultDetails || "No orphaned data found.") +
        '\n\n═══════════════════════════════════════════\n\n' +
        `📊 Total Records Deleted: ${result.totalOrphaned}\n` +
        `📁 Collections Processed: ${result.collectionsProcessed}\n` +
        `🔍 Collections with Orphans: ${result.collectionsWithOrphans}`;
      
      alert(successMessage);
      onRefresh();
    } catch (error: any) {
      const errorMessage = 
        '❌ Orphaned Data Cleanup Failed\n\n' +
        '═══════════════════════════════════════════\n\n' +
        `Error: ${error.message}\n\n` +
        'Please check the server logs for details.';
      
      alert(errorMessage);
      console.error('Cleanup error:', error);
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchValue("");
    onChange({ q: undefined, role: undefined, status: undefined, verified: undefined, dateFrom: undefined, dateTo: undefined, page: 1 });
  };

  const hasActiveFilters = !!(params.q || params.role || params.status || params.verified || params.dateFrom || params.dateTo);

  return (
    <Card className="mb-3">
      <Card.Body>
        <Row className="g-3">
          <UserFilterFields params={params} searchValue={searchValue} handleSearchChange={handleSearchChange} handleSearchKeyPress={handleSearchKeyPress} setSearchValue={setSearchValue} onChange={onChange} handleFilterChange={handleFilterChange} />
        </Row>
        {/* [2026-04-19] button-row-fix: Action buttons in own row — removed from filter Row to prevent overflow */}
        <div className="d-flex justify-content-end gap-2 mt-2">
          <UsersFiltersActions
            isExporting={isExporting}
            previewLoading={previewLoading}
            cleanupLoading={cleanupLoading}
            handleExport={handleExport}
            handlePreviewOrphanedData={handlePreviewOrphanedData}
            handleCleanOrphanedData={handleCleanOrphanedData}
            handleClearFilters={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
        <UserDateFilters params={params} handleFilterChange={handleFilterChange} handleClearFilters={handleClearFilters} onChange={onChange} />
      </Card.Body>

      {/* [2025-12-30] Data cleanup: Preview Modal */}
      <Modal 
        show={showPreviewModal} 
        onHide={() => !cleanupLoading && setShowPreviewModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton={!cleanupLoading}>
          <Modal.Title>
            <FontAwesomeIcon icon={faEye} className="me-2 text-warning" />
            Orphaned Data Preview
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {previewLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted">Scanning database collections...</p>
            </div>
          ) : previewData ? (
            <>
              {previewData.totalOrphaned === 0 ? (
                <Alert variant="success" className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faCheckCircle} size="2x" className="me-3" />
                  <div>
                    <h5 className="mb-1">No orphaned data found!</h5>
                    <p className="mb-0">All user references in the database are valid.</p>
                  </div>
                </Alert>
              ) : (
                <>
                  <Alert variant="warning" className="d-flex align-items-center mb-4">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="me-3" />
                    <div>
                      <h5 className="mb-1">Warning: Data Deletion</h5>
                      <p className="mb-0">This operation will permanently delete records associated with non-existent users. This action cannot be undone.</p>
                    </div>
                  </Alert>
                  
                  <div className="bg-light p-3 rounded mb-4">
                    <h6 className="text-uppercase text-muted small mb-3">Summary</h6>
                    <Row className="text-center">
                      <Col>
                        <div className="h4 mb-0 text-danger">{previewData.totalOrphaned}</div>
                        <div className="small text-muted">Orphaned Records</div>
                      </Col>
                      <Col>
                        <div className="h4 mb-0">{previewData.collectionsProcessed}</div>
                        <div className="small text-muted">Collections Scanned</div>
                      </Col>
                      <Col>
                        <div className="h4 mb-0 text-warning">{previewData.collectionsWithOrphans}</div>
                        <div className="small text-muted">Collections with Orphans</div>
                      </Col>
                    </Row>
                  </div>
                  
                  <h6>Records to be deleted:</h6>
                  <div className="table-responsive" style={{ maxHeight: '300px' }}>
                    <Table striped bordered hover size="sm">
                      <thead className="sticky-top bg-white">
                        <tr>
                          <th>Collection</th>
                          <th className="text-end">Orphaned Records</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(previewData)
                          .filter(([key]) => !['totalOrphaned', 'collectionsProcessed', 'collectionsWithOrphans'].includes(key))
                          .filter(([, value]) => typeof value === 'number' && value > 0)
                          .map(([collection, count]) => (
                            <tr key={collection}>
                              <td><code>{collection}</code></td>
                              <td className="text-end font-monospace">{count as number}</td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </>
          ) : (
            <Alert variant="danger">
              Failed to load preview data. Please try again or check server logs.
            </Alert>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <AdminButton
            intent="secondary"
            onClick={() => setShowPreviewModal(false)}
            disabled={cleanupLoading}
          >
            Cancel
          </AdminButton>
          
          {previewData && previewData.totalOrphaned > 0 && (
            <AdminButton
              intent="danger"
              onClick={handleConfirmCleanup}
              disabled={cleanupLoading}
            >
              {cleanupLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Deleting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                  Delete {previewData.totalOrphaned} Records
                </>
              )}
            </AdminButton>
          )}
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
